import { useCallback, useEffect, useState } from 'react';
import { useFetcher, useLocation } from 'react-router';
import { Button } from '@rocco/components/button';
import type { ArticleTileViewModel } from '@rocco/ui/tile-article/view-models/article';
import { EditorsEditArticleTile } from './EditorsEditArticleTile';
import { EDITORS_EDIT_ARTICLES_ACTION } from '~/routes/actions.editors-edit-articles';

const INITIAL_VISIBLE = 8;
const EXPANDED_VISIBLE = 12;

/**
 * The Editors' Edit section - Articles from Curated Collection.
 * Desktop only - hidden on mobile.
 *
 * Only renders on root /products route (not on category pages).
 * Displays 8 articles (2 rows) by default, expandable to 12 (3 rows).
 */
export const EditorsEdit = () => {
    const fetcher = useFetcher<{ articles: ArticleTileViewModel[] }>();
    const location = useLocation();

    // Only render on root /products
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const isProductsRoot =
        pathSegments.length === 1 && pathSegments[0] === 'products';

    const [expanded, setExpanded] = useState(false);

    // Fetch articles on mount (root page only)
    useEffect(() => {
        if (isProductsRoot) {
            fetcher.load(EDITORS_EDIT_ARTICLES_ACTION);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isProductsRoot]);

    // Handler for expanding articles
    const handleViewMore = useCallback(() => {
        setExpanded(true);
    }, []);

    // Don't render on category pages
    if (!isProductsRoot) {
        return null;
    }

    const articles = fetcher.data?.articles ?? [];
    const visibleArticles = articles.slice(
        0,
        expanded ? EXPANDED_VISIBLE : INITIAL_VISIBLE,
    );
    const isLoading =
        fetcher.state === 'loading' ||
        (fetcher.state === 'idle' && !fetcher.data);
    const hasNoArticles =
        fetcher.state === 'idle' && fetcher.data && articles.length === 0;
    const canExpand = !expanded && articles.length > INITIAL_VISIBLE;

    return (
        <section className="mb-[9.5rem] hidden md:mb-44 md:block">
            {/* Header */}
            <div className="flex flex-col px-6 md:px-12">
                <p className="mb-[2.25rem] text-[0.9375rem] leading-[1.22] font-medium tracking-[-0.75px] text-[#9a9a9a]">
                    Editorial
                </p>
                <h2 className="text-[4rem] leading-none font-medium tracking-[-3px] text-[#141414] lg:text-[5rem] xl:text-[6.25rem]">
                    The Editors' Edit,
                    <br />
                    <span className="text-[#a3a3a3]">in conversation.</span>
                </h2>
            </div>

            {/* Article Grid */}
            <div className="px-6 pt-[4.375rem] md:px-12">
                {isLoading ? (
                    <div className="grid grid-cols-4 gap-x-5 gap-y-[7.2rem]">
                        {[...Array(INITIAL_VISIBLE)].map((_, i) => (
                            <div key={i} className="flex flex-col">
                                <div className="aspect-440/666 animate-pulse rounded-lg bg-gray-200" />
                                <div className="flex flex-col">
                                    <div className="mt-[2.625rem] h-2.75 w-1/3 animate-pulse rounded bg-gray-200" />
                                    <div className="mt-[1rem] min-h-[5.06rem]">
                                        <div className="h-[2.2rem] w-full animate-pulse rounded bg-gray-200" />
                                        <div className="mt-1 h-[2.2rem] w-2/3 animate-pulse rounded bg-gray-200" />
                                    </div>
                                </div>
                                <div className="mt-[1rem] h-[3.375rem] w-full animate-pulse rounded bg-gray-200" />
                            </div>
                        ))}
                    </div>
                ) : hasNoArticles ? (
                    <div className="py-12 text-center">
                        <p className="text-base text-gray-600">
                            No featured articles available at this time.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-4 gap-x-5 gap-y-[7.2rem]">
                        {visibleArticles.map((article) => (
                            <EditorsEditArticleTile
                                key={article.id}
                                article={article}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* View More Button - only rendered when expandable; section mb-44 handles gap when absent */}
            {canExpand && (
                <div className="mt-30 flex flex-col items-center">
                    <Button
                        color="dark"
                        onClick={handleViewMore}
                        className="border border-[#141414] bg-[#141414] px-8 py-4 leading-[1.3] font-normal tracking-[0.32px] text-[#f2f2f2] shadow-[0px_4px_24px_0px_rgba(0,0,0,0.34)] transition-colors duration-200 hover:bg-white hover:text-[#141414]"
                    >
                        View More Recently Featured
                    </Button>
                </div>
            )}
        </section>
    );
};
